/**
 * `horizontalRuleNodeView` — 라이브러리 default `horizontal_rule.toDOM` 의 변형을 우회하는 NodeView.
 *
 * 배경:
 *   - ngwEditor 라이브러리의 기본 `horizontal_rule.toDOM` 가 `rawStyle` 이 있을 때 강제로
 *     `max-width: none; margin: 0; width: 100%; ` 를 prepend 한다 (라이브러리 default
 *     `.ngw-editor hr` 의 시각을 inline specificity 로 덮어쓰기 위함).
 *   - PM 이 그 결과를 DOM 에 부착하는 과정에서 브라우저 CSSOM 이 `border: none` shorthand 를
 *     `border-width: medium; border-style: none; border-color: currentcolor; border-image: initial`
 *     로 expand 하고, 색상도 `rgb(...)` 로 정규화한다.
 *   - 또한 atom block 노드라 `contenteditable="false"` 가 자동 부착된다.
 *   - 사용자가 setData 로 `<hr style="border: none; height: 1px; color: #e1e1e1; ...">` 를 입력해도
 *     editor 표시 시점에는 다음 형태로 변형되어 보였다:
 *     `<hr contenteditable="false" style="max-width: none; margin: 0px; width: 100%;
 *      border-width: medium; border-style: none; border-color: currentcolor; border-image: initial;
 *      height: 1px; color: rgb(225, 225, 225); background-color: rgb(225, 225, 225);">`.
 *
 * NodeView override 정책:
 *   - `rawStyle` 이 있는 경우(인용 헤더 separator / 외부 메일 hr): 원본 inline style 을 그대로
 *     `setAttribute('style', rawStyle)` 로 부착. 라이브러리 prepend 우회. setAttribute 는 HTML
 *     표준상 attribute 값을 raw 로 보존 (`element.style.cssText` 의 expand 와 별개).
 *   - `rawStyle` 이 없는 경우(본문 사용자가 직접 삽입한 hr): 라이브러리 default 와 동등하게
 *     `data-variant="..." class="ngw-hr ngw-hr--..."` 를 부착하여 ngw-hr variant 시각을 유지.
 *   - `update`: atom 노드라 거의 발화하지 않지만, attr 변경 시 dom 을 다시 동기화한다.
 *
 * 한계:
 *   - PM 이 atom block 노드 표면에 `contenteditable="false"` 를 자동 부착할 가능성은 NodeView 로
 *     제어가 어렵다. setData 진입 단계의 `normalizeHrInlineStyle` 가 `contenteditable` 을 제거하지만,
 *     PM 이 DOM 부착 후 다시 set 한다면 출력 단계에서 strip 하는 추가 처리가 필요할 수 있다.
 *   - 라이브러리 horizontal_rule schema 의 attrs 가 향후 변경되면 본 NodeView 도 동기 갱신 필요.
 */

import type { NodeViewFactory } from '@ngweditor/react'

type HrAttrs = {
  rawStyle?: string
  variant?: string
}

function applyHrAttrs(hr: HTMLHRElement, attrs: HrAttrs): void {
  // 모든 우리 부착 attribute 를 먼저 비운다 — update 경로의 stale state 방지.
  hr.removeAttribute('style')
  hr.removeAttribute('data-variant')
  hr.removeAttribute('class')

  if (typeof attrs.rawStyle === 'string' && attrs.rawStyle.length > 0) {
    // 원본 inline style 보존. setAttribute 는 raw 보존 — CSSOM expand 회피.
    hr.setAttribute('style', attrs.rawStyle)
    return
  }
  if (typeof attrs.variant === 'string' && attrs.variant.length > 0) {
    // 본문 사용자 hr — 라이브러리 ngw-hr variant 시각 유지.
    hr.setAttribute('data-variant', attrs.variant)
    hr.setAttribute('class', `ngw-hr ngw-hr--${attrs.variant}`)
  }
}

export const horizontalRuleNodeView: NodeViewFactory = (node) => {
  const hr = document.createElement('hr')
  applyHrAttrs(hr, node.attrs as HrAttrs)

  return {
    dom: hr,
    update(updatedNode) {
      if (updatedNode.type.name !== 'horizontal_rule') return false
      applyHrAttrs(hr, updatedNode.attrs as HrAttrs)
      return true
    },
  }
}
